import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { createFetchScheduler } from './fetch-scheduler'

/**
 * Creates a scheduler that periodically fetches data from a server via the given 'fetch' and/or 'diff'
 * functions created via 'createBoundActions' (see bound-actions.js) and passes the results to the Redux store.
 *
 * There are four modes of operation:
 * 1. Big refreshes only: The 'fetch' function is called periodically and replaces the content of the associated
 *    Redux store (by using the 'forceUpdate' flag of 'fetch').
 * 2. Periodic updates only: The 'diff' function is called periodically and updates the content of the associated
 *    Redux store via 'patch' operations. This assumes that the store has been filled beforehand. The backend server
 *    must implement the Differential GET protocol.
 * 3. Initial fetch plus periodic updates: The 'fetch' function is called initially. Then, periodic 'diff' requests
 *    update the store. The backend server must implement the Differential GET protocol.
 * 4. Interleaved big refreshes and periodic updates: Starting with a 'fetch' call, runs the 'fetch' function
 *    periodically (with big delays). In between the big-refresh calls, there are multiple 'diff' calls (with
 *    smaller delays). The big-refresh period must be a multiple of the small-refresh period.
 *    The backend server must implement the Differential GET protocol.
 *
 * @param {Object} config - The scheduler configuration
 * @param {Function} [config.fetch] - A redux thunk action for complete fetches (see bound-actions.js)
 * @param {number} [config.fetchPeriod] - The time difference between two fetch runs (in milliseconds)
 * @param {Function} [config.diff] - A redux thunk action for differential fetches (see bound-actions.js)
 * @param {number} [config.diffPeriod] - The time difference between two diff runs (in milliseconds)
 * @returns {Boolean} whether the scheduler is currently fetching initial data (after changes to the fetch/diff function)
 */
function useFetchScheduler(options) {
    const [isLoading, setLoading] = useState(false)
    const isStarted = useRef(false)
    const dispatch = useDispatch()
    const scheduler = useRef(createFetchScheduler(options))
    const { diff, fetch } = options ?? {}

    useEffect(() => {
        const updateScheduler = async () => {
            if (isStarted.current) {
                setLoading(true)
                await dispatch(scheduler.current.update({ fetch, diff }))
                setLoading(false)
            }
        }
        updateScheduler()
    }, [fetch, diff, dispatch])

    useEffect(() => {
        const currentScheduler = scheduler.current
        const startScheduler = async () => {
            isStarted.current = true
            setLoading(true)
            await dispatch(currentScheduler.start())
            setLoading(false)
        }
        startScheduler()
        return () => dispatch(currentScheduler.stop())
    }, [dispatch])

    return isLoading
}

export { useFetchScheduler }
